import type { ReactNode } from 'react';
import type {
    CellContext,
    Filter,
    FilterOperator,
    FilterType,
    ResolvedColumn,
} from '../../domain';
import type { OperatorOption, Option } from './types';

export const NUMBER_OPERATORS: OperatorOption[] = [
    { value: 'equals', label: 'Equal to' },
    { value: 'greaterThan', label: 'Greater than' },
    { value: 'greaterThanOrEqual', label: 'Greater than or equal' },
    { value: 'lessThan', label: 'Less than' },
    { value: 'lessThanOrEqual', label: 'Less than or equal' },
];

export const DATE_OPERATORS: OperatorOption[] = [
    { value: 'equals', label: 'On' },
    { value: 'lessThan', label: 'Before' },
    { value: 'lessThanOrEqual', label: 'On or before' },
    { value: 'greaterThan', label: 'After' },
    { value: 'greaterThanOrEqual', label: 'On or after' },
];

export function columnKey<T>(col: ResolvedColumn<T>): string {
    return col.filterKey ?? col.id;
}

export function groupColumns<T>(
    columns: ResolvedColumn<T>[],
): Record<string, ResolvedColumn<T>[]> {
    return columns.reduce<Record<string, ResolvedColumn<T>[]>>((acc, col) => {
        const key = col.groupHeader ?? '';
        (acc[key] ??= []).push(col);
        return acc;
    }, {});
}

export function filterableColumns<T>(
    columns: ResolvedColumn<T>[],
    appliedKeys: Set<string>,
    query: string,
): ResolvedColumn<T>[] {
    const q = query.trim().toLowerCase();
    const open = columns.filter(
        (c) => c.canFilter && !appliedKeys.has(columnKey(c)),
    );
    if (!q) return open;
    return open.filter(
        (c) =>
            c.header.toLowerCase().includes(q) ||
            Boolean(c.groupHeader?.toLowerCase().includes(q)),
    );
}

export function filterOptionsByQuery(
    options: Option[],
    query: string,
): Option[] {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
        (o) =>
            o.label.toLowerCase().includes(q) ||
            o.value.toLowerCase().includes(q),
    );
}

export function chipValue<T>(
    col: ResolvedColumn<T> | undefined,
    value: string,
): ReactNode {
    if (!col) return value;
    if (col.filter === 'set' && col.render) {
        return col.render({ value, column: col } as unknown as CellContext<T>);
    }
    return col.labels?.[value] ?? value;
}

const OPERATOR_BY_TYPE: Partial<Record<FilterType, FilterOperator>> = {
    text: 'contains',
    set: 'oneOf',
};

export function buildFilter<T>(
    col: ResolvedColumn<T>,
    value: string | string[],
    operator?: FilterOperator,
): Filter {
    const type = col.filter as FilterType;
    return {
        type,
        operator: operator ?? OPERATOR_BY_TYPE[type] ?? 'equals',
        value,
    };
}

function operatorLabelMap(
    operators: OperatorOption[],
): Partial<Record<FilterOperator, string>> {
    return Object.fromEntries(operators.map((o) => [o.value, o.label]));
}
const DATE_OPERATOR_LABEL = operatorLabelMap(DATE_OPERATORS);
const NUMBER_OPERATOR_LABEL = operatorLabelMap(NUMBER_OPERATORS);

export function operatorLabelFor(
    type: FilterType,
    operator: FilterOperator,
): string | undefined {
    if (type === 'date') return DATE_OPERATOR_LABEL[operator];
    if (type === 'number') return NUMBER_OPERATOR_LABEL[operator];
    return undefined;
}

export function inputTypeFor<T>(
    col: ResolvedColumn<T> | null,
): 'search' | 'number' | 'text' {
    if (!col) return 'search';
    return col.filter === 'number' ? 'number' : 'text';
}

export function placeholderFor<T>(
    col: ResolvedColumn<T> | null,
    fallback?: string,
): string {
    if (!col) return fallback ?? 'Search';
    if (col.filter === 'set') return `Filter "${col.header}" options`;
    if (col.filter === 'date') return 'e.g. 2026-04-14';
    if (col.filter === 'number') return 'Enter value';
    return `Value for "${col.header}"`;
}
